import React, { FormEvent, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowRightIcon, CheckIcon } from 'lucide-react';
import { ConsentCheckbox } from './ConsentCheckbox';
import { saveSubmission, SUBMISSION_ERROR } from '../utils/submissions';
type Status = 'idle' | 'sending' | 'sent';
import { DialCodeSelect } from './DialCodeSelect';
import { useDialCode } from '../hooks/useDialCode';
const ease = [0.22, 1, 0.36, 1] as const;
export function CallbackBar() {
  const [code, setCode] = useDialCode();
  const [phone, setPhone] = useState('');
  const [error, setError] = useState('');
  const [status, setStatus] = useState<Status>('idle');
  const [consent, setConsent] = useState(false);
  const [consentError, setConsentError] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const digits = phone.replace(/\D/g, '');
    const badPhone = digits.length < 6;
    setError(badPhone ? 'Please enter a valid number — it is never shared.' : '');
    setConsentError(!consent);
    if (badPhone || !consent) return;
    setStatus('sending');
    const ok = await saveSubmission({
      source: 'callback_bar',
      phone: `${code} ${phone}`,
      details: {
        consent: true
      }
    });
    if (ok) {
      setStatus('sent');
    } else {
      setError(SUBMISSION_ERROR);
      setStatus('idle');
    }
  };
  return <section aria-labelledby="callback-heading" className="bg-sand px-6 py-14 text-ink md:px-16 md:py-16" data-id="element-140" data-mp="rdy5d6e74odu">
      <div className="mx-auto grid max-w-[1320px] grid-cols-1 items-center gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] lg:gap-20" data-id="element-141" data-mp="ifmfj6l5lumg">
        <div data-id="element-142" data-mp="4gwkdwhwa50q">
          <h2 id="callback-heading" className="font-editorial text-[34px] leading-[38px] md:text-[44px] md:leading-[48px]" data-id="element-143" data-mp="4h681dfgw039">
            One call. No one else need know.
          </h2>
          <p className="mt-3 max-w-[480px] text-[16px] font-light leading-[26px] text-ink/75" data-id="element-144" data-mp="1yuxgwapz2fxn">
            Leave a number and we will call you, discreetly, to explore what Superluxury can arrange for you.
          </p>
        </div>

        <AnimatePresence mode="wait" initial={false} data-id="element-145" data-mp="3btpm2z3o8p5j">
          {status === 'sent' ? <motion.div key="sent" role="status" initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.5,
          ease
        }} className="flex items-start gap-4" data-id="element-146" data-mp="36rrwzo8lb04o">
              <span className="mt-1 flex h-8 w-8 shrink-0 items-center justify-center bg-ink text-paper" data-id="element-147" data-mp="3ul06cxew63og">
                <CheckIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-148" data-mp="3qjoh4szxjkgs" />
              </span>
              <div data-id="element-149" data-mp="3griqpnpbtb3p">
                <p className="text-[20px] font-normal" data-id="element-150" data-mp="3gs0wpc1sj3ot">Received, in confidence</p>
                <p className="mt-1 text-[16px] font-light text-ink/75" data-id="element-151" data-mp="i1imyd6viksh">
                  We will call {code} {phone} from a private line within the hour.
                </p>
                <button type="button" onClick={() => {
              setStatus('idle');
              setPhone('');
            }} className="mt-4 border-b border-ink/40 pb-0.5 text-[15px] font-medium transition-colors duration-300 hover:border-ink" data-id="element-152" data-mp="d8pgnkvrb4ea">
                  Use another number
                </button>
              </div>
            </motion.div> : <motion.form key="form" onSubmit={submit} noValidate initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.3,
          ease
        }} aria-label="Request a private call" data-id="element-153" data-mp="13jrklcyzcwd6">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-end" data-id="element-154" data-mp="1ibry0aq8nb61">
                <div className="flex flex-1 items-end gap-4 border-b border-ink" data-id="element-155" data-mp="1mcvemzg41b8b">
                  <DialCodeSelect id="callback-code" value={code} onChange={setCode} className="pb-3 text-[18px]" data-id="element-156" data-mp="1rbf1d22j2r1q" />
                  <span className="mb-3 h-5 w-px bg-ink/25" aria-hidden="true" data-id="element-157" data-mp="1dm3n9pvyilxp" />
                  <label className="sr-only" htmlFor="callback-phone" data-id="element-158" data-mp="19l08l6pnavy2">
                    Phone number
                  </label>
                  <input id="callback-phone" type="tel" inputMode="tel" autoComplete="tel-national" value={phone} onChange={e => {
                setPhone(e.target.value);
                if (error) setError('');
              }} placeholder="Your number" aria-invalid={Boolean(error)} aria-describedby={error ? 'callback-error' : undefined} className="min-w-0 flex-1 bg-transparent pb-3 text-[18px] placeholder:font-light placeholder:text-ink/55 focus:outline-none" data-id="element-159" data-mp="1ncof8uh4ks7n" />
                </div>
                <button type="submit" disabled={status === 'sending'} className="group inline-flex h-[52px] items-center justify-center gap-3 whitespace-nowrap bg-ink px-8 text-[16px] font-medium text-paper transition-opacity duration-150 hover:opacity-85 disabled:opacity-60" data-id="element-160" data-mp="3t0njciy6xt1b">
                  {status === 'sending' ? 'Sealing…' : 'Request a private call'}
                  <ArrowRightIcon className="h-4 w-4 transition-transform duration-500 ease-luxe group-hover:translate-x-1" strokeWidth={1.5} aria-hidden="true" data-id="element-161" data-mp="gj0i55a27o5q" />
                </button>
              </div>
              {error && <p id="callback-error" role="alert" className="mt-3 text-[14px] text-ink" data-id="element-162" data-mp="br8y7dlesgok">
                  {error}
                </p>}
              <ConsentCheckbox id="callback-consent" checked={consent} onChange={v => {
            setConsent(v);
            if (v) setConsentError(false);
          }} error={consentError} className="mt-4" data-id="element-163" data-mp="148us9f2zuyma" />
            </motion.form>}
        </AnimatePresence>
      </div>
    </section>;
}